import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { GapCursorSelection, Side as GapCursorSide, hideCaretModifier, JSON_ID, setGapCursorAtPos, Side } from '@atlaskit/editor-common/selection';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { findPositionOfNodeBefore } from '@atlaskit/editor-prosemirror/utils';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { CellSelection } from '@atlaskit/editor-tables/cell-selection';
import { selectionPluginKey } from '../types';
import { gapCursorPluginKey } from './gap-cursor-plugin-key';
import { deleteNode } from './gap-cursor/actions';
import { Direction } from './gap-cursor/direction';
import { getLayoutModeFromTargetNode, isIgnoredClick } from './gap-cursor/utils';
import { toDOM } from './gap-cursor/utils/place-gap-cursor';
var plugin = new SafePlugin({
  key: gapCursorPluginKey,
  state: {
    init: function init() {
      return {
        selectionIsGapCursor: false,
        displayGapCursor: true,
        hideCursor: false
      };
    },
    // @ts-ignore - Workaround for help-center local consumption

    apply: function apply(tr, pluginState, _oldState, newState) {
      var _meta$displayGapCurso, _selectionMeta$hideCu;
      var meta = tr.getMeta(gapCursorPluginKey);
      var selectionMeta = tr.getMeta(selectionPluginKey);
      var selectionIsGapCursor = newState.selection instanceof GapCursorSelection;
      return {
        selectionIsGapCursor: selectionIsGapCursor,
        // only attempt to hide gap cursor if selection is gap cursor
        displayGapCursor: selectionIsGapCursor ? (_meta$displayGapCurso = meta === null || meta === void 0 ? void 0 : meta.displayGapCursor) !== null && _meta$displayGapCurso !== void 0 ? _meta$displayGapCurso : pluginState.displayGapCursor : true,
        // track hideCursor state from selection plugin
        hideCursor: (_selectionMeta$hideCu = selectionMeta === null || selectionMeta === void 0 ? void 0 : selectionMeta.hideCursor) !== null && _selectionMeta$hideCu !== void 0 ? _selectionMeta$hideCu : pluginState.hideCursor
      };
    }
  },
  // @ts-ignore - Workaround for help-center local consumption

  view: function view(_view) {
    /**
     * If the selection is at the beginning of a document and is a NodeSelection,
     * convert to a GapCursor selection. This is to stop users accidentally replacing
     * the first node of a document by accident.
     */
    if (_view.state.selection.anchor === 0 && _view.state.selection instanceof NodeSelection) {
      // This is required otherwise the dispatch doesn't trigger in the correct place
      window.requestAnimationFrame(function () {
        _view.dispatch(_view.state.tr.setSelection(new GapCursorSelection(_view.state.doc.resolve(0), GapCursorSide.LEFT)));
      });
    }
    return {
      // @ts-ignore - Workaround for help-center local consumption
      update: function update(view) {
        var _gapCursorPluginKey$g = gapCursorPluginKey.getState(view.state),
          selectionIsGapCursor = _gapCursorPluginKey$g.selectionIsGapCursor;
        /**
         * Starting with prosemirror-view 1.19.4, cursor wrapper that previously was hiding cursor doesn't exist:
         * https://github.com/ProseMirror/prosemirror-view/commit/4a56bc7b7e61e96ef879d1dae1014ede0fc09e43
         *
         * Because it was causing issues with RTL: https://github.com/ProseMirror/prosemirror/issues/948
         *
         * This is the work around which uses `caret-color: transparent` in order to hide regular caret,
         * when gap cursor is visible.
         *
         * Browser support is pretty good: https://caniuse.com/#feat=css-caret-color
         */
        view.dom.classList.toggle(hideCaretModifier, selectionIsGapCursor);
      }
    };
  },
  props: {
    decorations: function decorations(editorState) {
      var doc = editorState.doc,
        selection = editorState.selection;
      var _gapCursorPluginKey$g2 = gapCursorPluginKey.getState(editorState),
        displayGapCursor = _gapCursorPluginKey$g2.displayGapCursor,
        hideCursor = _gapCursorPluginKey$g2.hideCursor;
      if (selection instanceof GapCursorSelection && displayGapCursor && !hideCursor) {
        var $from = selection.$from,
          side = selection.side;

        // render decoration DOM node always to the left of the target node even if selection points to the right
        // otherwise positioning of the right gap cursor is a nightmare when the target node has a nodeView with vertical margins
        var position = selection.head;
        var isRightCursor = side === Side.RIGHT;
        if (isRightCursor && $from.nodeBefore) {
          var nodeBeforeStart = findPositionOfNodeBefore(selection);
          if (typeof nodeBeforeStart === 'number') {
            position = nodeBeforeStart;
          }
        }
        var node = isRightCursor ? $from.nodeBefore : $from.nodeAfter;
        var layoutMode = node && getLayoutModeFromTargetNode(node);
        return DecorationSet.create(doc, [Decoration.widget(position, toDOM, {
          key: "".concat(JSON_ID, "-").concat(side, "-").concat(layoutMode),
          side: layoutMode ? -1 : 0
        })]);
      }
      return null;
    },
    // render gap cursor only when its valid
    // @ts-ignore - Workaround for help-center local consumption
    createSelectionBetween: function createSelectionBetween(view, $anchor, $head) {
      if (view && view.state && view.state.selection instanceof CellSelection) {
        // Do not show GapCursor when there is a CellSection happening
        return null;
      }
      if ($anchor.pos === $head.pos && GapCursorSelection.valid($head)) {
        return new GapCursorSelection($head);
      }
      return null;
    },
    // @ts-ignore - Workaround for help-center local consumption
    handleClick: function handleClick(view, nodePos, event) {
      var _$pos$parent;
      var posAtCoords = view.posAtCoords({
        left: event.clientX,
        top: event.clientY
      });
      if (!posAtCoords || isIgnoredClick(event.target instanceof HTMLElement ? event.target : null)) {
        return false;
      }
      var isInsideTheTarget = posAtCoords.pos === posAtCoords.inside;
      if (isInsideTheTarget) {
        return false;
      }
      var leftSideOffsetX = 20;
      var side = event.offsetX > leftSideOffsetX ? Side.RIGHT : Side.LEFT;
      var $pos = view.state.doc.resolve(nodePos);
      // In the new prosemirror-view posAtCoords  is not returning a precise value for our media nodes
      if (((_$pos$parent = $pos.parent) === null || _$pos$parent === void 0 ? void 0 : _$pos$parent.type.name) === 'mediaSingle') {
        var $insidePos = view.state.doc.resolve(Math.max(posAtCoords.inside, 0));
        // We don't have GapCursors problems when the node target is inside the root level
        if ($insidePos.depth <= 1) {
          return false;
        }
        var mediaGapCursor = !$pos.nodeBefore ? $pos.before() : $pos.after();
        return setGapCursorAtPos(mediaGapCursor, side)(view.state, view.dispatch);
      }
      var docSize = view.state.doc.content.size;
      var nodeInside = posAtCoords.inside < 0 || posAtCoords.inside > docSize ? null : view.state.doc.nodeAt(posAtCoords.inside);
      if (nodeInside !== null && nodeInside !== void 0 && nodeInside.isAtom) {
        return false;
      }
      return setGapCursorAtPos(nodePos, side)(view.state, view.dispatch);
    },
    // @ts-ignore - Workaround for help-center local consumption

    handleDOMEvents: {
      /**
       * Android composition events aren't handled well by Prosemirror
       * We've added a couple of beforeinput hooks to help PM out when trying to delete
       * certain nodes. We can remove these when PM has better composition support.
       * @see https://github.com/ProseMirror/prosemirror/issues/543
       */
      // Ignored via go/ees005
      // @ts-ignore - Workaround for help-center local consumption
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      beforeinput: function beforeinput(view, event) {
        if (event.inputType === 'deleteContentBackward' && view.state.selection instanceof GapCursorSelection) {
          event.preventDefault();
          return deleteNode(Direction.BACKWARD)(view.state, view.dispatch);
        }
        return false;
      }
    }
  }
});
export default plugin;